import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import {
    BaseStyle,
    BaseStyleEnums,
    ButtonType,
    DatePicker,
    DatePickerEnums,
    Select,
    SelectTypes,
    TabBar,
    TimeZoneEnums,
} from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import commonMessages from '~/core/commonMessages';
import { CalendarIcon } from '~/core/customIcons';
import { getDateRangeStringForWeek } from '~/core/dateTimeFormats';
import { useDateParam } from '~/core/hooks/useDateParam';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { GameDay } from '~/core/types';
import { isPageZoomed, parseValueFromLocalizedAttribute } from '~/core/utils';
import { useLocationQuery } from '~/redux/ducks/location';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import BackToTopButton from '~/views/components/backToTopButton/BackToTopButton';
import DayCard from '~/views/components/dayCard/DayCard';
import { Days, DaysDisplayContainer } from '~/views/components/dayCard/styles';
import HowToWatchModal from '~/views/components/howToWatchModal/HowToWatchModal';
import { HowToWatchData } from '~/views/components/howToWatchModal/types';
import NarrowOddsProvider from '~/views/components/oddsProvider/NarrowOddsProvider';
import OddsProviderDayCard from '~/views/components/oddsProvider/OddsProviderDayCard';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';
import TimezonePicker from '~/views/components/timezonePicker/TimezonePicker';
import { SelectableTimezone } from '~/views/components/timezonePicker/types';
import { useHowToWatchQuery } from '~/views/pages/dotcom/gameCenter/api';

import { useScheduleCalendarQuery } from './api';
import CalendarView from './components/calendarView/CalendarView';
import PrintButton from './components/calendarView/components/printButton/PrintButton';
import ListView from './components/listView/ListView';
import { ScheduleViewType } from './enums';
import { ClubSchedule, Schedule as ScheduleType, ScheduleTab, ViewProps } from './types';

import {
    AdContainer,
    ButtonsContainer,
    Control,
    Controls,
    ExpandFilterButton,
    Filters,
    Header,
    LeftHandFilters,
    LinkContainer,
    LinkText,
    MobileDaysContainer,
    OddsProviderWrapper,
    SelectContainer,
    SmallLeagueFilters,
    TabBarContainer,
} from './styles';

const NOW = 'now';

const Schedule: FC = () => {
    const teamAbbrev = window.__SITE_SETTINGS__.franchise.triCode.toUpperCase();
    const isClubPage = teamAbbrev !== 'NHL';
    const navigate = useNavigate();
    const params = useParams();
    const dateFromURL = useDateParam();
    const intl = useIntl();
    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;
    const isLarge = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;
    // As the default view is the calendar view, we instead check to make sure that the view is not the list, nor season view
    const isCalendarView =
        params.view !== ScheduleViewType.LIST && params.view !== ScheduleViewType.SEASON && isClubPage;

    const [expandedFilters, setExpandedFilters] = useState(isClubPage);
    const [timezone, setTimezone] = useState<SelectableTimezone>();
    const [scheduleDate, setScheduleDate] = useState<DateTime>();
    const [scheduleSeason, setScheduleSeason] = useState<number>();
    const [schedule, setSchedule] = useState<ScheduleType | ClubSchedule>();
    const [isStickyHeader, setIsStickyHeader] = useState(false);
    const [selectedDate, setSelectedDate] = useState<DateTime>();
    const [clubCalendarLink, setClubCalendarLink] = useState('');
    const [isShowingNarrowOdds, setIsShowingNarrowOdds] = useState<boolean>(true);
    const [lastVisitedDate, setLastVisitedDate] = useState<DateTime>();
    const [resolvedRedirectDate, setResolvedRedirectDate] = useState<DateTime>();
    const [howToWatchGameId, setHowToWatchGameId] = useState<number | null>(null);
    const [howToWatchModalData, setHowToWatchModalData] = useState<HowToWatchData | null>(null);
    const [isDisplayingHowToWatchModal, setIsDisplayingHowToWatchModal] = useState<boolean>(false);

    const headerRef = useRef<HTMLDivElement>(null);

    const { data: countryCode } = useLocationQuery({});
    const oddsPartner = schedule?.oddsPartners?.find((partner) => partner.country === countryCode);

    const onScrollChange = useCallback(() => {
        const top = headerRef.current?.getBoundingClientRect().top || Infinity;
        const headerHeight = isClubPage ? BaseStyle.CLUB_HEADER_HEIGHT : BaseStyle.LEAGUE_HEADER_HEIGHT;
        const padding = isMedium ? 10 : 8;

        setIsStickyHeader(headerHeight + padding >= top && !isPageZoomed());
    }, [isMedium, isClubPage]);

    useScroll({
        onScrollChanged: onScrollChange,
    });

    const clubTimezone = useMemo(() => {
        if (isClubPage && schedule && schedule.clubTimezone) {
            return schedule.clubTimezone;
        }

        return undefined;
    }, [isClubPage, schedule]);

    const {
        data: scheduleCalendar,
        isLoading: isLoadingScheduleCalendar,
        isInitialLoad: isInitialScheduleCalendarLoad,
        resolvedDate,
    } = useScheduleCalendarQuery(scheduleDate?.toISODate() || undefined);

    useEffect(() => {
        if (resolvedRedirectDate === undefined && resolvedDate !== undefined) {
            setResolvedRedirectDate(resolvedDate);
        }
    }, [resolvedRedirectDate, resolvedDate]);

    const DEFAULT_TEAM: SelectTypes.BasicOption = useMemo(
        () => ({
            label: intl.formatMessage({
                description: 'An option indicating that all teams should be shown',
                defaultMessage: 'All Teams',
            }),
            key: 'all',
            value: 'all',
        }),
        [intl]
    );

    const teamOptions = useMemo<Array<SelectTypes.BasicOption>>(() => {
        let options: Array<SelectTypes.BasicOption> = [];

        scheduleCalendar.teams?.forEach((team) => {
            options.push({
                label: parseValueFromLocalizedAttribute(team.name),
                key: team.id,
                value: team.abbrev.toLowerCase(),
            });
        });

        options = options.sort((option1, option2) => {
            if (option1.label && option1.label) {
                return (option1.label as string).localeCompare(option2.label as string);
            }
            return 0;
        });

        options.unshift(DEFAULT_TEAM);

        return options;
    }, [DEFAULT_TEAM, scheduleCalendar.teams]);

    const pageTitle = useMemo(() => {
        if (isClubPage) {
            return intl.formatMessage(
                {
                    description: 'Title for the Club Schedule page, to be displayed on a browser tab etc.',
                    defaultMessage: '{clubName} Schedule | {clubName}',
                },
                {
                    clubName: window.__SITE_SETTINGS__.franchise.name,
                }
            );
        }

        if (scheduleDate !== undefined) {
            return intl.formatMessage(
                {
                    description: 'Title for the League Schedule page, to be displayed on a browser tab etc.',
                    defaultMessage: 'Schedule | {date} | NHL.com',
                },
                {
                    date: scheduleDate.toLocaleString(DateTime.DATE_MED),
                }
            );
        }

        return undefined;
    }, [scheduleDate, intl, isClubPage]);

    const selectedTeamValue = isClubPage ? teamAbbrev.toLowerCase() : params.franchise || DEFAULT_TEAM.value;
    const selectedTeam = teamOptions.find((team) => team.value === selectedTeamValue);

    const navigateTo = (newDate: DateTime, team: string | number | undefined, newSeason?: number): void => {
        const teamPath = isClubPage || team === DEFAULT_TEAM.value ? '' : `/${team}`;
        const viewPath = params.view ? `/${params.view}` : '';
        let datePath: string | number | null;

        if (params.view === ScheduleViewType.SEASON) {
            if (newSeason) {
                datePath = newSeason;
            } else {
                datePath = newDate.toLocaleString({ year: 'numeric' });
            }
        } else {
            datePath = newDate.toISODate();
        }

        navigate(`/schedule/${datePath}${teamPath}${viewPath}`);
    };

    const updateDate = (newDate: DateTime): void => navigateTo(newDate, selectedTeam?.value);

    const handleNextOrPreviousDate = (newDate: DateTime, el?: HTMLButtonElement) => {
        document.getElementById('root')?.scrollIntoView({ behavior: 'smooth' });
        navigateTo(newDate, selectedTeam?.value);
        el?.blur();
    };

    const handleTeamUpdated = (e, team: SelectTypes.BasicOption): void => {
        scheduleDate && navigateTo(scheduleDate, team.value);
    };

    const handleArrowClick = (isLeft: boolean): void => {
        let newDate: DateTime;
        let newSeason: number | undefined = undefined;

        if (scheduleDate) {
            if (isClubPage && params.view === undefined) {
                newDate = scheduleDate.plus({ months: isLeft ? -1 : 1 });
            } else if (params.view === ScheduleViewType.SEASON && scheduleSeason) {
                newDate = scheduleDate;
                newSeason = Number(`${scheduleSeason}`.substring(0, 4)) + (isLeft ? -1 : 1);
            } else {
                newDate = scheduleDate.plus({ days: isLeft ? -7 : 7 });
            }

            navigateTo(newDate, selectedTeam?.value, newSeason);
        }
    };

    const handleDateUpdated = (day: GameDay): void => {
        document.getElementById(day.date)?.scrollIntoView();
    };

    const dateDisplay = (date: DateTime): string => {
        if (date !== undefined) {
            if (params.view === undefined && isClubPage) {
                return date.toLocaleString({ month: 'short', year: 'numeric' });
            } else if (params.view === ScheduleViewType.SEASON) {
                return `${scheduleSeason}`.substring(0, 4);
            }
            return getDateRangeStringForWeek(date, intl);
        }
        return '';
    };

    const ViewComponent: FC<ViewProps> = useMemo(() => {
        if (isClubPage) {
            if (params.view === ScheduleViewType.LIST || params.view === ScheduleViewType.SEASON) {
                return ListView;
            } else {
                return CalendarView;
            }
        }

        return ListView;
    }, [isClubPage, params]);

    const maxDatePickerDetail = useMemo(() => {
        switch (params.view) {
            case ScheduleViewType.LIST:
                return DatePickerEnums.CalendarViewType.day;
            case ScheduleViewType.SEASON:
                return DatePickerEnums.CalendarViewType.year;
            default:
                return isClubPage ? DatePickerEnums.CalendarViewType.month : DatePickerEnums.CalendarViewType.day;
        }
    }, [params, isClubPage]);

    const selectableTabs = useMemo<Array<ScheduleTab>>(() => {
        if (scheduleDate && scheduleCalendar && isClubPage) {
            const date = lastVisitedDate !== undefined ? lastVisitedDate : scheduleDate;
            const shouldUseResolvedRedirectDate =
                dateFromURL === NOW ||
                dateFromURL.length === 4 ||
                DateTime.fromISO(dateFromURL).month === resolvedRedirectDate?.month;

            const calendarTab: ScheduleTab = {
                key: ScheduleViewType.CALENDAR,
                label: intl.formatMessage({
                    description: 'Label which indicates that when a user clicks it they will see a calendar view.',
                    defaultMessage: 'Calendar',
                }),
                link: `/schedule/${date.toISODate()}`,
                ariaControlId: `${ScheduleViewType.CALENDAR}-tabpanel`,
            };

            const listTab: ScheduleTab = {
                key: ScheduleViewType.LIST,
                label: intl.formatMessage({
                    description: 'Label which indicates that when a user clicks it they will see a list view of games.',
                    defaultMessage: 'List',
                }),
                link: `/schedule/${
                    shouldUseResolvedRedirectDate ? resolvedRedirectDate?.toISODate() : date.toISODate()
                }/list`,
                ariaControlId: `${ScheduleViewType.LIST}-tabpanel`,
            };

            const clubSeason = scheduleCalendar.teams.find((team) => team.abbrev === teamAbbrev)?.seasonId;

            const seasonTab: ScheduleTab = {
                key: ScheduleViewType.SEASON,
                label: intl.formatMessage({
                    description:
                        'Label which indicates that when a user clicks it they will see a full season view of games.',
                    defaultMessage: 'Full Season',
                }),
                link: `/schedule/${clubSeason ? `${`${clubSeason}`.substring(0, 4)}/` : ''}fullseason`,
                ariaControlId: `${ScheduleViewType.SEASON}-tabpanel`,
            };

            return [calendarTab, listTab, seasonTab];
        }

        return [];
    }, [
        scheduleDate,
        scheduleCalendar,
        isClubPage,
        lastVisitedDate,
        dateFromURL,
        resolvedRedirectDate,
        intl,
        teamAbbrev,
    ]);

    const onSelectedTab = (tab: ScheduleTab) => {
        if (tab.key === ScheduleViewType.SEASON) {
            setLastVisitedDate(scheduleDate);
        } else {
            setLastVisitedDate(undefined);
        }
        navigate(tab.link);
    };

    const ariaLabels = useMemo(
        () => ({
            teamSelect: {
                dropdownButton: intl.formatMessage({
                    defaultMessage: 'Expand Team Options',
                    description:
                        'Label for select element button, prompting user to expand team options in a dropdown menu',
                }),
                optionsBox: intl.formatMessage({
                    defaultMessage: 'Team Options',
                    description: 'Label indicating that user is viewing a list of team options for a select component',
                }),
            },
        }),
        [intl]
    );

    const daysDisplay = !isClubPage && schedule && scheduleDate && selectedDate && (
        // only add special bottom margin when display is not a sticky header, and between medium and large screen breakpoints
        <DaysDisplayContainer $addBottomMargin={!isStickyHeader && isMedium && !isLarge}>
            <Days ref={headerRef} $padBottom={isStickyHeader && (isMedium || !isShowingNarrowOdds || !oddsPartner)}>
                {(schedule as ScheduleType).gameWeek?.map((day) => (
                    <DayCard
                        key={day.date}
                        day={day}
                        isSelected={DateTime.fromISO(day.date).hasSame(selectedDate, 'day')}
                        isToday={DateTime.fromISO(day.date).hasSame(scheduleDate, 'day')}
                        onDateUpdated={handleDateUpdated}
                        urlRoot="/schedule"
                    />
                ))}
                {isStickyHeader && oddsPartner && isMedium && <OddsProviderDayCard bettingOddPartner={oddsPartner} />}
            </Days>
            {isStickyHeader && oddsPartner && (
                <OddsProviderWrapper>
                    <NarrowOddsProvider
                        bettingOddPartner={oddsPartner}
                        show={isShowingNarrowOdds}
                        setShow={setIsShowingNarrowOdds}
                    />
                </OddsProviderWrapper>
            )}
        </DaysDisplayContainer>
    );

    const teamSelect = !isClubPage && (
        <SelectContainer>
            <Select
                aria-label={intl.formatMessage(commonMessages.tableHeaders.long.team)}
                dropdownButtonAriaLabel={ariaLabels.teamSelect.dropdownButton}
                onOptionChange={handleTeamUpdated}
                options={teamOptions}
                optionsBoxAriaLabel={ariaLabels.teamSelect.optionsBox}
                selectedOption={selectedTeam}
            />
        </SelectContainer>
    );

    const timezonePicker = (
        <TimezonePicker defaultTimezone={clubTimezone} onChangeTimezone={setTimezone} selectedTimezone={timezone} />
    );

    // this prevents rerenders further down; previously a useEffect in CalendarView.tsx was being fired far too often thanks to its dependency on setScheduleDate
    const scheduleDateCallback = useCallback(
        (newDate: DateTime) => {
            setScheduleDate(newDate);
            setSelectedDate(newDate);
        },
        [setScheduleDate, setSelectedDate]
    );

    const { data: gameHowToWatchData, isLoading: isGameHowToWatchDataLoading } = useHowToWatchQuery(
        Number(howToWatchGameId)
    );

    useEffect(() => {
        setHowToWatchModalData(gameHowToWatchData);
    }, [gameHowToWatchData]);

    const onHowToWatchClick = (gameId: number | null): void => {
        setHowToWatchGameId(gameId);
        setIsDisplayingHowToWatchModal(true);
    };

    const onCloseModal = () => {
        setHowToWatchGameId(null);
        setIsDisplayingHowToWatchModal(false);
        setHowToWatchModalData(null);
    };

    return isInitialScheduleCalendarLoad && !isClubPage ? (
        <LoadingDisplay isLoading={true} />
    ) : (
        <PageWrapper defaultPageLayout hasExternalBackground pageTitle={pageTitle} theme={BaseStyle.LightTheme}>
            <LoadingDisplay isLoading={isLoadingScheduleCalendar}>
                <Controls $isStickyHeader={!isClubPage && isStickyHeader}>
                    <Control $isClubPage={isClubPage}>
                        <Header $isClubPage={isClubPage}>
                            <FormattedMessage description="A header for the schedule page" defaultMessage="Schedule" />
                            {window.__AD_SETTINGS__.presbyDivId && (
                                <AdTag divId={window.__AD_SETTINGS__.presbyDivId} adType={AdType.PRESBY} />
                            )}
                        </Header>
                        <Filters $isStickyHeader={isStickyHeader}>
                            <LeftHandFilters>
                                <DatePicker
                                    dateDisplay={dateDisplay}
                                    intl={intl}
                                    isDisplayingFullWeek={true}
                                    isDisplayingMonth={isCalendarView}
                                    localeCode={window.__SITE_SETTINGS__.localeCode}
                                    onArrowClick={handleArrowClick}
                                    onSelectDate={updateDate}
                                    selectedDate={scheduleDate}
                                    maxDetail={maxDatePickerDetail}
                                    minDetail={DatePickerEnums.CalendarViewType.year}
                                />
                                {isMedium && teamSelect}
                                {isMedium && timezonePicker}
                            </LeftHandFilters>
                            {isClubPage && isMedium && (
                                <ButtonsContainer>
                                    <PrintButton isMedium={isMedium} isCalendarView={isCalendarView} intl={intl} />
                                    <LinkContainer
                                        href={clubCalendarLink || undefined}
                                        $isMedium={isMedium}
                                        $isDisabled={!clubCalendarLink}
                                        title={
                                            clubCalendarLink
                                                ? undefined
                                                : intl.formatMessage({
                                                      description:
                                                          'Message indicating that a downloadable calendar will be available in the future.',
                                                      defaultMessage: 'Downloadable Calendar not yet available',
                                                  })
                                        }
                                        aria-label={
                                            clubCalendarLink
                                                ? intl.formatMessage(commonMessages.ariaLabels.links.calendar)
                                                : undefined
                                        }
                                    >
                                        <CalendarIcon />
                                    </LinkContainer>
                                </ButtonsContainer>
                            )}
                        </Filters>
                        {isMedium && daysDisplay}
                        {isClubPage && !isMedium && timezonePicker}
                        {isClubPage && clubCalendarLink && !isMedium && (
                            <ButtonsContainer>
                                <PrintButton isMedium={isMedium} isCalendarView={isCalendarView} intl={intl} />
                                <LinkContainer href={clubCalendarLink} $isMedium={isMedium}>
                                    <LinkText>
                                        <FormattedMessage
                                            description="A phrase describing a button that adds a schedule to your calendar"
                                            defaultMessage="Add to Cal."
                                        />
                                    </LinkText>
                                    <CalendarIcon />
                                </LinkContainer>
                            </ButtonsContainer>
                        )}
                        {isClubPage && scheduleDate && (
                            <TabBarContainer>
                                <TabBar
                                    ref={headerRef}
                                    tabs={selectableTabs}
                                    selectedTab={
                                        selectableTabs.find((view) => view.key === params.view) || selectableTabs[0]
                                    }
                                    onChangeSelectedTab={onSelectedTab}
                                />
                            </TabBarContainer>
                        )}
                    </Control>
                    {window.__AD_SETTINGS__.mrecDivId && (
                        <AdContainer $isHidden={!isClubPage && isStickyHeader}>
                            <AdTag divId={window.__AD_SETTINGS__.mrecDivId} adType={AdType.MEDIUM_RECTANGLE} />
                        </AdContainer>
                    )}
                </Controls>
                {!isMedium && !isClubPage && (
                    <>
                        <MobileDaysContainer $isStickyHeader={isStickyHeader}>{daysDisplay}</MobileDaysContainer>
                        <SmallLeagueFilters>
                            {expandedFilters && timezonePicker}
                            {expandedFilters && teamSelect}
                            <ExpandFilterButton
                                variant={ButtonType.Text}
                                onClick={() => setExpandedFilters(!expandedFilters)}
                                disableHover={true}
                            >
                                <FormattedMessage
                                    {...commonMessages.misc.callToAction[
                                        expandedFilters ? 'hideFilters' : 'showFilters'
                                    ]}
                                />
                            </ExpandFilterButton>
                        </SmallLeagueFilters>
                    </>
                )}
                {isStickyHeader &&
                    createPortal(
                        <BackToTopButton
                            shouldCenter={isClubPage}
                            onClick={() => document.getElementById('root')?.scrollIntoView({ behavior: 'smooth' })}
                        />,
                        document.body
                    )}
                {selectedTeam && (
                    <ViewComponent
                        dateFromURL={dateFromURL}
                        handleNextOrPreviousDate={handleNextOrPreviousDate}
                        isLeaguePage={!isClubPage}
                        isFullSeason={params.view === ScheduleViewType.SEASON}
                        onHowToWatchClick={onHowToWatchClick}
                        selectedTeam={selectedTeam}
                        setScheduleDate={scheduleDateCallback}
                        setScheduleSeason={setScheduleSeason}
                        setSelectedDate={setSelectedDate}
                        setClubCalendarLink={setClubCalendarLink}
                        setSchedule={setSchedule}
                        timezone={(timezone?.value as TimeZoneEnums) || clubTimezone}
                        isMedium={isMedium}
                        resolvedRedirectDate={resolvedRedirectDate}
                    />
                )}
                {isDisplayingHowToWatchModal && (
                    <HowToWatchModal
                        howToWatch={howToWatchModalData}
                        isLoading={isGameHowToWatchDataLoading}
                        onCloseModal={onCloseModal}
                    />
                )}
            </LoadingDisplay>
        </PageWrapper>
    );
};

export { Schedule as Component };
